import axios from 'axios'
import type { OpenPermitListItem } from 'modules/permit/api/permitService'
import { isClosedStatus, isOpenStatus } from 'modules/permit/constants/permitStatus'

export const normalizeGuid = (value?: string | null) => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  return /^[0-9a-fA-F-]{36}$/.test(trimmed) ? trimmed.toLowerCase() : null
}

export const resolveMenuRoleId = (menus: Array<Record<string, any>> | null | undefined, routePath: string) => {
  const normalizedRoutePath = routePath.trim().toLowerCase()

  for (const menu of menus ?? []) {
    const candidatePath = typeof menu?.routePath === 'string' ? menu.routePath.trim().toLowerCase() : ''
    if (!candidatePath) continue
    const normalizedCandidatePath = candidatePath.startsWith('/') ? candidatePath : `/${candidatePath}`
    if (normalizedCandidatePath !== normalizedRoutePath) continue

    const normalizedRoleId =
      normalizeGuid(menu?.roleId) ??
      normalizeGuid(menu?.role_id) ??
      normalizeGuid(menu?.id)

    if (normalizedRoleId) {
      return normalizedRoleId
    }
  }

  return null
}

export const resolveAccessibleLocationIds = (
  user?: Record<string, any>,
  roles?: Array<Record<string, any>> | null,
  activeRoleId?: string | null,
) => {
  const results = new Set<string>()

  const normalizedActiveRoleId = normalizeGuid(activeRoleId)
  const roleRows = Array.isArray(roles) ? roles : []

  const matchingRoleRows = normalizedActiveRoleId
    ? roleRows.filter((role) => {
        const roleId =
          normalizeGuid(role?.roleId) ??
          normalizeGuid(role?.role_id) ??
          normalizeGuid(role?.id)
        return roleId === normalizedActiveRoleId
      })
    : roleRows

  const sourceRoleRows = matchingRoleRows.length > 0 ? matchingRoleRows : roleRows

  if (!normalizedActiveRoleId || matchingRoleRows.length === 0) {
    ;[
      user?.locationIds,
      user?.location_ids,
      user?.workAreaIds,
      user?.work_area_ids,
      user?.accessibleLocationIds,
      user?.accessible_location_ids,
      user?.locations,
    ].forEach((candidate) => {
      if (!Array.isArray(candidate)) return
      candidate.forEach((item) => {
        const normalized = normalizeGuid(typeof item === 'string' ? item : item?.locationId ?? item?.id)
        if (normalized) results.add(normalized)
      })
    })
  }

  sourceRoleRows.forEach((role) => {
    const normalized =
      normalizeGuid(role?.departmentId) ??
      normalizeGuid(role?.department_id) ??
      normalizeGuid(role?.locationId) ??
      normalizeGuid(role?.location_id)
    if (normalized) {
      results.add(normalized)
    }
  })

  return Array.from(results)
}

export const getErrorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    const responseMessage = (error.response?.data as { message?: string } | undefined)?.message
    return responseMessage || error.response?.statusText || error.message || fallback
  }
  return error instanceof Error ? error.message || fallback : fallback
}

export const formatDate = (value?: string | null, withTime = false) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString('th-TH', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  })
}

export const formatTime = (value?: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleTimeString('th-TH', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const formatDateRange = (start?: string | null, end?: string | null) => {
  return end ? `${formatDate(start)} - ${formatDate(end)}` : formatDate(start)
}

export const formatPermitDuration = (start?: string | null, end?: string | null) => {
  if (!start || !end) return '-'

  const startDate = new Date(start)
  const endDate = new Date(end)
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return '-'

  const diffMs = Math.max(0, endDate.getTime() - startDate.getTime())
  const totalMinutes = Math.floor(diffMs / 60000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  return `${hours} \u0e0a\u0e21. ${minutes} \u0e19\u0e32\u0e17\u0e35`
}

export const isOpenedPermitStatus = (status?: string | null) => {
  return isOpenStatus(status)
}

export const isClosedPermitStatus = (status?: string | null) => {
  return isClosedStatus(status)
}

export const resolvePermitTypeBadge = (permitTypeCode?: string | null, permitTypeName?: string | null) => {
  const normalizedCode = (permitTypeCode ?? '').trim().toUpperCase()
  const normalizedName = (permitTypeName ?? '').trim().toLowerCase()

  switch (normalizedCode) {
    case 'HOT_WORK':
      return { className: 'bg-red-600 text-white border-red-600', label: 'HOT WORK' }
    case 'COLD_WORK':
      return { className: 'bg-blue-600 text-white border-blue-600', label: 'COLD WORK' }
    case 'CONFINE_WORK':
      return { className: 'bg-orange-500 text-white border-orange-500', label: 'CONFINE WORK SPACE' }
    case 'EXCAVATION_WORK':
      return { className: 'bg-amber-600 text-white border-amber-600', label: 'EXCAVATION WORK' }
    default:
      break
  }

  if (normalizedName.includes('hot work')) return { className: 'bg-red-600 text-white border-red-600', label: 'HOT WORK' }
  if (normalizedName.includes('cold work')) return { className: 'bg-blue-600 text-white border-blue-600', label: 'COLD WORK' }
  if (normalizedName.includes('confine')) return { className: 'bg-orange-500 text-white border-orange-500', label: 'CONFINE WORK SPACE' }

  return {
    className: 'bg-slate-100 text-slate-700 border-slate-200',
    label: permitTypeName || permitTypeCode || 'PERMIT',
  }
}

export const formatSupervisorSummary = (permit: OpenPermitListItem) => {
  const supervisorNames = (permit.supervisors ?? []).map((item) => item.employeeName).filter(Boolean)
  if (supervisorNames.length === 0) return '\u0e22\u0e31\u0e07\u0e44\u0e21\u0e48\u0e23\u0e30\u0e1a\u0e38'
  if (supervisorNames.length > 2) return `${supervisorNames.slice(0, 2).join(', ')} \u0e41\u0e25\u0e30\u0e2d\u0e35\u0e01 ${supervisorNames.length - 2} \u0e04\u0e19`
  return supervisorNames.join(', ')
}
